import {
    Alert,
    AlertTitle,
    Box,
    Card,
    Chip,
    ListSubheader,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import * as React from "react";
import { TableLoadingIndicator } from "./DataTable";
import { RefreshButton } from "../CommonIcons";
import { getItemTypeLabel, getPageSelectionList, QueryTableProps } from "./QueryTableCommon";
import { EmptyTableContent } from "./TableCommon";
import { QueryTableFilter } from "./QueryTableFilter";
import { QueryTableSort } from "./QueryTableSort";
import { useGlobalTableSortFilterState } from "./TableFilterState";
import { useTranslation } from "react-i18next";

export const QueryTable = <TData extends unknown, TError extends unknown>(p: QueryTableProps<TData, TError>) => {
    const { t, i18n } = useTranslation();
    const {
        refetch,
        title,
        queryKey,
        error,
        isLoading,
        isError,
        data,
        pageCount,
        columns,
        pagination,
        setPagination,
        emptyTableConfig,
        totalItems,
        itemTypeLabel,
    } = p;
    const globalSortFilterState = useGlobalTableSortFilterState();
    const refreshSortFilterConfig = globalSortFilterState.refreshSortFilterConfig;

    React.useEffect(() => {
        refreshSortFilterConfig(queryKey);
    }, [i18n.resolvedLanguage, queryKey, refreshSortFilterConfig]);

    const refreshButton = (
        <Box pr={2}>
            <RefreshButton onClick={() => refetch()} variant={"outlined"} color={"inherit"} />
        </Box>
    );

    const getColumnVisibility = () => {
        const colVisibility: { [key: string]: boolean } = {};
        p.hiddenColumns?.forEach((c) => {
            colVisibility[c] = false;
        });
        return colVisibility;
    };
    const table = useReactTable<TData>({
        data: data || [],
        getCoreRowModel: getCoreRowModel(),
        columns: columns,
        pageCount: pageCount ? pageCount + 1 : undefined,
        state: {
            pagination: pagination || { pageIndex: 1, pageSize: 10 },
            columnVisibility: getColumnVisibility(),
        },
        onPaginationChange: setPagination,
        manualPagination: !!pagination,
    });

    if (isError) {
        return (
            <Box width={"100%"} p={2}>
                <Alert severity={"error"} action={refreshButton}>
                    <AlertTitle>{t("common:table.errorEncountered")}</AlertTitle>
                    <Box display={"flex"} justifyContent={"space-between"}>
                        <Box>{(error as unknown as Error)?.message}</Box>
                    </Box>
                </Alert>
            </Box>
        );
    }

    const tableContent = (
        <>
            <Table>
                <TableHead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableCell key={header.id} variant={"head"}>
                                    <Box sx={(header.column.columnDef.meta as any)?.style}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {isLoading && <TableLoadingIndicator />}

                    {table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id} hover>
                            {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id} variant={"body"}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                    {data?.length === 0 && !isLoading && (
                        <TableRow>
                            <TableCell colSpan={columns.length} align={"center"}>
                                <Box pt={6} pb={6}>
                                    <EmptyTableContent emptyTableConfig={emptyTableConfig} isFilterActive={globalSortFilterState.getIsFilterActive(queryKey)} />
                                </Box>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </>
    );

    const paginationContent = pagination ? (
        <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"flex-end"} pt={2} pb={2} width={"100%"}>
            <Pagination
                count={table.getPageCount() - 1}
                page={table.getState().pagination?.pageIndex || 1}
                onChange={(event, page) => {
                    table.setPageIndex(page);
                }}
                defaultPage={1}
                siblingCount={0}
            />
            <Typography variant={"caption"} color={"textSecondary"}>
                {t("common:table.goToPage")}
            </Typography>
            <Box>
                <Select
                    value={table.getState().pagination?.pageIndex || 1}
                    variant={"outlined"}
                    MenuProps={{
                        slotProps: {
                            paper: {
                                sx: {
                                    height: "200px",
                                    "&::-webkit-scrollbar": {
                                        width: "10px",
                                        backgroundColor: "transparent",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        borderRadius: "10px",
                                        border: "2px solid transparent",
                                        backgroundColor: "rgba(255,255,255,.1)",
                                        backgroundClip: `content-box`,
                                    },
                                },
                            },
                        },
                    }}
                    slotProps={{}}
                    onChange={(event) => {
                        table.setPageIndex(Number(event.target.value));
                    }}
                    sx={{ height: "35px", lineHeight: 0, width: "70px" }}
                    renderValue={(value: unknown) => <Typography variant={"caption"}>{value as React.ReactNode}</Typography>}
                >
                    {getPageSelectionList(table.getPageCount() - 1).map((p, i) => {
                        return (
                            <MenuItem key={i} value={p}>
                                {p}
                            </MenuItem>
                        );
                    })}
                </Select>
            </Box>
        </Stack>
    ) : null;

    if (isError) {
        return (
            <Card>
                <Box width={"100%"} p={2}>
                    <Alert severity={"error"} action={refreshButton}>
                        <AlertTitle>{t("common:table.errorEncountered")}</AlertTitle>
                        <Box display={"flex"} justifyContent={"space-between"}>
                            <Box>{(error as unknown as Error).message}</Box>
                        </Box>
                    </Alert>
                </Box>
            </Card>
        );
    }

    return (
        <>
            {!p.hideToolbar && (
                <Grid container justifyContent={"space-between"} mb={2}>
                    <Grid>
                        <QueryTableFilter queryKey={queryKey} refetchQuery={refetch} />
                    </Grid>
                    <Grid>
                        <QueryTableSort queryKey={queryKey} refetchQuery={refetch} />
                    </Grid>
                </Grid>
            )}
            <TableContainer component={p.tableComponent ?? Card}>
                {!!title && <ListSubheader>{title}</ListSubheader>}
                {tableContent}
                {(!!pagination || totalItems > 0) && data?.length > 0 && (
                    <Grid container justifyContent={"space-between"} spacing={2} alignItems={"center"} pr={2} pl={2}>
                        {totalItems && (
                            <Grid>
                                <Typography variant={"caption"} color={"textSecondary"}>
                                    {t("common:table.total", {
                                        count: totalItems.toLocaleString(i18n.resolvedLanguage),
                                        item: getItemTypeLabel(itemTypeLabel || t("common:table.item"), totalItems, i18n.resolvedLanguage),
                                    })}
                                </Typography>
                            </Grid>
                        )}
                        {pagination && <Grid>{paginationContent}</Grid>}
                    </Grid>
                )}
            </TableContainer>
        </>
    );
};
